import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { holidayApi } from '@/services/api';
import api from '@/services/api';
import { format } from 'date-fns';
import { Upload, Download, Trash2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';

const HOLIDAY_TYPES = ['PUBLIC', 'OPTIONAL', 'OFFICE_CLOSURE'];

export default function HolidaysPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: '', name: '', type: 'PUBLIC' });

  const { data: holidays = [], isLoading } = useQuery({
    queryKey: ['holidays', year],
    queryFn: async () => {
      const res = await holidayApi.getAll(year);
      return res.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => holidayApi.remove(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['holidays'] }); toast.success('Holiday removed'); },
    onError: () => toast.error('Failed to remove'),
  });

  const addMutation = useMutation({
    mutationFn: () => holidayApi.create(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      toast.success('Holiday added');
      setShowAdd(false);
      setForm({ date: '', name: '', type: 'PUBLIC' });
    },
    onError: () => toast.error('Failed to add holiday'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/holidays/upload-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      toast.success(res.data.message);
    },
    onError: () => toast.error('CSV upload failed'),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = '';
  };

  const downloadCSV = () => {
    const header = 'date,name,type\n';
    const rows = (holidays as any[]).map((h: any) =>
      `${format(new Date(h.date), 'yyyy-MM-dd')},${h.name},${h.type}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `holidays_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const typeColor = (type: string) => {
    if (type === 'PUBLIC') return 'bg-green-100 text-green-700';
    if (type === 'OPTIONAL') return 'bg-blue-100 text-blue-700';
    return 'bg-orange-100 text-orange-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 bg-white"
          >
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="secondary" size="sm" onClick={downloadCSV}>
            <Download className="w-4 h-4 mr-1.5" /> Download CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
          <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadMutation.isPending}>
            <Upload className="w-4 h-4 mr-1.5" /> {uploadMutation.isPending ? 'Uploading...' : 'Upload CSV'}
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Add Holiday
          </Button>
        </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2 text-xs text-blue-700">
        CSV format: <code className="font-mono">date,name,type</code> &mdash; e.g. <code className="font-mono">2026-08-15,Independence Day,PUBLIC</code>. Types: PUBLIC &middot; OPTIONAL &middot; OFFICE_CLOSURE
      </div>

      {showAdd && (
        <div className="card p-4 border border-blue-200 bg-blue-50/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-700">Add Holiday</h3>
            <button onClick={() => setShowAdd(false)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-slate-500 mb-1 block">Name</label>
              <input type="text" placeholder="e.g. Independence Day" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                {HOLIDAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Button size="sm" onClick={() => addMutation.mutate()} disabled={!form.date || !form.name || addMutation.isPending}>
              {addMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">#</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Date</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Day</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Holiday Name</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Type</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400">Loading...</td></tr>
            ) : (holidays as any[]).length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400">No holidays for {year}. Upload a CSV or add manually.</td></tr>
            ) : (
              (holidays as any[]).map((h: any, i: number) => (
                <tr key={h.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-700">{format(new Date(h.date), 'dd MMM yyyy')}</td>
                  <td className="px-4 py-3 text-slate-500">{format(new Date(h.date), 'EEEE')}</td>
                  <td className="px-4 py-3 text-slate-800">{h.name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${typeColor(h.type)}`}>
                      {h.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => deleteMutation.mutate(h.id)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {(holidays as any[]).length > 0 && (
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-400">
            {(holidays as any[]).length} holidays in {year}
          </div>
        )}
      </div>
    </div>
  );
}
